import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll'
import { Container, Row, Col } from 'react-grid-system';
import jstz from 'jstz';
import { getLocalizedText } from '@vp-dst/localization-util';

import {
    setSelectedTimezone,
    setSelectedDate,
    setSelectedTime,
    setName,
    setEmail,
    setPhone,
    setNameError,
    setEmailError,
    setPhoneError,
    setNameErrorsSuppressed,
    setEmailErrorsSuppressed,
    setPhoneErrorsSuppressed,
    setSelectedDesigner,
} from 'redux/actionCreators';

import {
    getAvailableDates,
    getAvailableTimes,
    submitCoDesignOrder,
    submitDifyOrder,
} from 'redux/thunkActionCreators';

import {
    fireSchedulePageHit,
    fireTimezonePickerChangedTimezone,
    fireDatePickerChangedDate,
    fireTimePickerChangedTime,
    fireCoDesignCheckoutCTAClicked,
    firePreviousDesignerCount,
    fireSchedulePageStandardDifyCheckoutCTAClicked,
} from 'utils/Tracking';

import {
} from 'utils/Tracking.js';

import Scheduler from 'components/Scheduler';
import { defaultTextStyle } from 'settings/theme';
import TextField from 'components/vistaprintUILibrary/TextField';
import ErrorMessage from 'components/vistaprintUILibrary/ErrorMessage';
import Button from 'components/vistaprintUILibrary/Button';
import LoadingBox from 'components/vistaprintUILibrary/LoadingBox';
import InputValidationUtil from 'utils/InputValidationUtil';
import DifyOrderDTO from 'services/designServices/dtos/DifyOrderDTO';
import CoDesignOrderDTO from 'services/designServices/dtos/CoDesignOrderDTO';
import timezones from 'data/timezones.json';
import DSBriefModel from 'models/DSBriefModel';
import { siteCulture } from 'settings/vistaprintContext';

class Scheduling extends React.Component {
    constructor(props) {
        super(props);

        this.selectedDateOnChange = this.selectedDateOnChange.bind(this);
        this.selectedTimeOnChange = this.selectedTimeOnChange.bind(this);
        this.timezoneOnChange = this.timezoneOnChange.bind(this);
        this.nameOnChange = this.nameOnChange.bind(this);
        this.emailOnChange = this.emailOnChange.bind(this);
        this.phoneOnChange = this.phoneOnChange.bind(this);
        this.nameOnBlur = this.nameOnBlur.bind(this);
        this.emailOnBlur = this.emailOnBlur.bind(this);
        this.phoneOnBlur = this.phoneOnBlur.bind(this);
        this.refreshAvailableDates = this.refreshAvailableDates.bind(this);
        this.refreshAvailableTimes = this.refreshAvailableTimes.bind(this);
        this.isBriefComplete = this.isBriefComplete.bind(this);
        this.coDesignCheckoutOnClick = this.coDesignCheckoutOnClick.bind(this);
        this.difyCheckoutOnClick = this.difyCheckoutOnClick.bind(this);
        this.selectedDesignerOnChange = this.selectedDesignerOnChange.bind(this);
    }

    componentDidMount() {
        const {
            previousDesigners,
            selectedTimezone,
            setSelectedTimezone,
            getAvailableDates,
        } = this.props;

        setTimeout(fireSchedulePageHit, 1000);

        scroll.scrollTo(0, {
            duration: 50
        });

        // if we haven't detected a timezone yet, detect it now
        if (!selectedTimezone) {
            try {
                let defaultTimezone = timezones[siteCulture].length === 1 ? timezones[siteCulture][0].value : null;

                const detectedTimezone = jstz.determine();
                const matchingTimezone = timezones[siteCulture].find(timezone => timezone.value === detectedTimezone.name());
                if (matchingTimezone) {
                    defaultTimezone = matchingTimezone.value;
                }
                if (defaultTimezone) {
                    setSelectedTimezone(defaultTimezone);
                }
            } catch (e) {
                // This will almost never get hit because the app would bomb out in multiple places 
                // before it gets here if a site culture didn't exist in timezones
                this.setAvailableDatesError = getLocalizedText('timezones-unavailable');
            }
        } else {
            /* 
             * In most cases, this block won't be executed.
             * Typically, selectedTimezone will not be set, and we will set it in the above block. That will eventually trigger getAvailableDates in componentDidUpdate
             * But in the case that we already have the timezone when we get here, we have to get the dates at this point because componentDidUpdate won't fire
             */
            getAvailableDates(selectedTimezone);
        }

        const prevDesignerCount = (previousDesigners && previousDesigners.length) || 0;
        firePreviousDesignerCount(prevDesignerCount);
    }

    componentDidUpdate(prevProps) {
        const {
            selectedTimezone,
            selectedDate,
            getAvailableDates,
            getAvailableTimes,
            selectedDesigner,
        } = this.props;

        const {
            selectedTimezone: prevSelectedTimezone,
            selectedDate: prevSelectedDate,
            selectedDesigner: prevSelectedDesigner,
        } = prevProps;

        // if the timezone or designer changed, get new dates and times
        if (selectedTimezone !== prevSelectedTimezone || selectedDesigner !== prevSelectedDesigner) {
            getAvailableDates(selectedTimezone)
            getAvailableTimes(selectedDate, selectedTimezone);
        }

        // if the date or designer changed, get new times
        if (selectedDate !== prevSelectedDate || selectedDesigner !== prevSelectedDesigner) {
            getAvailableTimes(selectedDate, selectedTimezone);
        }
    }

    refreshAvailableDates() {
        const {
            selectedTimezone,
            getAvailableDates,
        } = this.props;

        getAvailableDates(selectedTimezone);
    }

    refreshAvailableTimes() {
        const {
            selectedTimezone,
            selectedDate,
            getAvailableTimes,
        } = this.props;

        getAvailableTimes(selectedDate, selectedTimezone);
    }

    timezoneOnChange(newTimezone) {
        const {
            selectedTimezone,
            setSelectedTimezone,
        } = this.props;

        if (selectedTimezone !== newTimezone) {
            fireTimezonePickerChangedTimezone(newTimezone);
            setSelectedTimezone(newTimezone);
        }
    }

    selectedDateOnChange(date, dayOfTheWeek) { // dayOfTheWeek is for tracking
        const {
            selectedDate,
            setSelectedDate,
        } = this.props;


        if (date !== selectedDate) {
            fireDatePickerChangedDate(dayOfTheWeek);

            setSelectedDate(date);
        }
    }

    selectedTimeOnChange(time, timeOfDay) { // timeOfDay is for tracking
        const {
            selectedTime,
            setSelectedTime,
        } = this.props;

        if (time !== selectedTime) {
            fireTimePickerChangedTime(timeOfDay);

            setSelectedTime(time);
        }
    }

    nameOnChange(event) {
        if (event && event.target) {
            const {
                name: prevName,
                nameError: prevNameError,
                setName,
                setNameError,
            } = this.props;

            const name = event.target.value;

            if (name !== prevName) {
                setName(name);
            }

            let nameError = null;
            if (name === '') {
                nameError = getLocalizedText('name-is-required');
            }

            if (nameError !== prevNameError) {
                setNameError(nameError);
            }
        }
    }

    emailOnChange(event) {
        if (event && event.target) {
            const {
                email: prevEmail,
                emailError: prevEmailError,
                setEmail,
                setEmailError,
            } = this.props;

            const email = event.target.value;

            if (email !== prevEmail) {
                setEmail(email);
            }

            let emailError = null;
            if (email === '') {
                emailError = getLocalizedText('email-is-required');
            } else if (!InputValidationUtil.isValidEmail(email)) {
                emailError = getLocalizedText('please-enter-a-valid-email');
            }

            if (emailError !== prevEmailError) {
                setEmailError(emailError);
            }
        }
    }

    phoneOnChange(event) {
        if (event && event.target) {
            const {
                phone: prevPhone,
                phoneError: prevPhoneError,
                setPhone,
                setPhoneError,
            } = this.props;

            const phone = event.target.value;

            if (phone !== prevPhone) {
                setPhone(phone);
            }

            let phoneError = null;
            if (phone === '') {
                phoneError = getLocalizedText('phone-number-is-required');
            } else if (!InputValidationUtil.isValidPhoneNumber(phone)) {
                phoneError = getLocalizedText('please-enter-a-valid-phone number');
            }

            if (phoneError !== prevPhoneError) {
                setPhoneError(phoneError);
            }
        }
    }

    // input validation errors are suppressed until the first blur event
    nameOnBlur() {
        const {
            nameErrorsSuppressed,
            setNameErrorsSuppressed,
        } = this.props;

        if (nameErrorsSuppressed) {
            setNameErrorsSuppressed(false);
        }
    }

    emailOnBlur() {
        const {
            emailErrorsSuppressed,
            setEmailErrorsSuppressed,
        } = this.props;

        if (emailErrorsSuppressed) {
            setEmailErrorsSuppressed(false);
        }
    }

    phoneOnBlur() {
        const {
            phoneErrorsSuppressed,
            setPhoneErrorsSuppressed,
        } = this.props;

        if (phoneErrorsSuppressed) {
            setPhoneErrorsSuppressed(false);
        }
    }

    isBriefComplete() {
        const {
            selectedTime,
            name,
            email,
            phone,
            nameError,
            emailError,
            phoneError,
        } = this.props;

        return (
            selectedTime !== null &&
            name.length > 0 &&
            email.length > 0 &&
            phone.length > 0 &&
            nameError === null &&
            emailError === null &&
            phoneError === null
        );
    }

    coDesignCheckoutOnClick() {
        if (!this.isBriefComplete()) {
            return;
        }

        if (window.tagMouseFlow) {
            window.tagMouseFlow('CoDesignAddToCart');
        }

        const {
            selectedDesigner,
            selectedProduct,
            selectedTimezone,
            selectedTime, // selectedTime contains the full datetime string
            name,
            email,
            phone,
            selectedProductConfigurations,
            note,
            uploads,
            uploadRefs,
            submitCoDesignOrder,
        } = this.props;

        const coDesignOrderDTO = new CoDesignOrderDTO({
            calendarId: selectedDesigner && selectedDesigner.length > 0 ? selectedDesigner : null,
            productName: selectedProduct.name,
            productPfid: selectedProduct.pfid,
            productConfigurations: selectedProductConfigurations,
            note,
            uploads,
            uploadRefs,
            name,
            email,
            phone,
            timezone: selectedTimezone,
            appointmentDateTimeString: selectedTime,
            difyServiceQuantity: selectedProduct.priceMultiplier,
        });

        fireCoDesignCheckoutCTAClicked(coDesignOrderDTO.calendarId);

        submitCoDesignOrder(coDesignOrderDTO);
    }

    difyCheckoutOnClick() {
        if (window.tagMouseFlow) {
            window.tagMouseFlow('DifyAddToCart');
        }

        const {
            selectedProduct,
            selectedProductConfigurations,
            note,
            uploads,
            uploadRefs,
            submitDifyOrder,
        } = this.props;

        const difyOrderDTO = new DifyOrderDTO({
            productName: selectedProduct.name,
            productPfid: selectedProduct.pfid,
            productConfigurations: selectedProductConfigurations,
            note,
            uploads,
            uploadRefs,
            difyServiceQuantity: selectedProduct.priceMultiplier,
        });

        fireSchedulePageStandardDifyCheckoutCTAClicked();

        submitDifyOrder(difyOrderDTO);
    }

    selectedDesignerOnChange(event) {
        if (event && event.target) {
            const {
                setSelectedDesigner
            } = this.props;

            setSelectedDesigner(event.target.value);
        }
    }

    render() {
        const {
            selectedProduct,
            selectedTimezone,
            selectedDate,
            selectedTime,
            name,
            email,
            phone,

            nameError,
            emailError,
            phoneError,

            nameErrorsSuppressed,
            emailErrorsSuppressed,
            phoneErrorsSuppressed,

            previousDesigners,
            selectedDesigner,

            availableDates,
            availableTimes,

            availableDatesLoading,
            availableTimesLoading,

            setAvailableDatesError,
            setAvailableTimesError,

            orderSubmitting,
            submitCoDesignOrderError,
        } = this.props;

        const headingStyle = {
            ...defaultTextStyle,
            fontSize: '2em',
        };

        if (!DSBriefModel.altDocId && !selectedProduct) {
            return (<Redirect to="/" />);
        }

        return (
            <Container fluid xs sm md>
                <Row>
                    <Col xs={12}>
                        <div
                            style={{
                                ...headingStyle,
                                marginBottom: 20,
                            }}
                        >
                            {getLocalizedText('1-enter-your-information')}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={4}>
                        <TextField
                            id="name"
                            label={getLocalizedText("name")}
                            required={true}
                            fullWidth={true}
                            autoCompleteCategory="name"
                            value={name}
                            onChange={this.nameOnChange}
                            onBlur={this.nameOnBlur}
                            errorMessage={nameErrorsSuppressed ? null : nameError}
                            success={name.length > 0 && !nameError}
                            containerStyle={{
                                marginBottom: 20,
                            }}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <TextField
                            id="email"
                            label={getLocalizedText("email")}
                            required={true}
                            fullWidth={true}
                            autoCompleteCategory="email"
                            value={email}
                            onChange={this.emailOnChange}
                            onBlur={this.emailOnBlur}
                            errorMessage={emailErrorsSuppressed ? null : emailError}
                            success={email.length > 0 && !emailError}
                            containerStyle={{
                                marginBottom: 20,
                            }}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <TextField
                            id="phone"
                            label={getLocalizedText("phone")}
                            required={true}
                            fullWidth={true}
                            autoCompleteCategory="tel"
                            value={phone}
                            onChange={this.phoneOnChange}
                            onBlur={this.phoneOnBlur}
                            errorMessage={phoneErrorsSuppressed ? null : phoneError}
                            success={phone.length > 0 && !phoneError}
                            containerStyle={{
                                marginBottom: 20,
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <div style={headingStyle}>{getLocalizedText('2-choose-a-day-and-time')}</div>
                    </Col>
                </Row>
                <Scheduler
                    selectedTimezone={selectedTimezone}
                    timezoneOnChange={this.timezoneOnChange}
                    timezones={timezones[siteCulture]}

                    previousDesigners={previousDesigners}
                    selectedDesigner={selectedDesigner}
                    selectedDesignerOnChange={this.selectedDesignerOnChange}

                    selectedDate={selectedDate}
                    selectedDateOnChange={this.selectedDateOnChange}
                    availableDates={availableDates}
                    availableDatesLoading={availableDatesLoading}
                    setAvailableDatesError={setAvailableDatesError}
                    refreshAvailableDates={this.refreshAvailableDates}

                    selectedTime={selectedTime}
                    selectedTimeOnChange={this.selectedTimeOnChange}
                    availableTimes={availableTimes}
                    availableTimesLoading={availableTimesLoading}
                    setAvailableTimesError={setAvailableTimesError}
                    refreshAvailableTimes={this.refreshAvailableTimes}
                />
                <Row>
                    <Col>
                        <div
                            style={{
                                marginTop: 30,

                                textAlign: 'center',
                            }}
                        >
                            <span
                                style={{
                                    ...defaultTextStyle,
                                    fontSize: '1.3em',
                                    marginRight: 5,
                                }}
                            >
                                {getLocalizedText('dont-see-a-time-that-works')}
                            </span>
                            <a
                                onClick={this.difyCheckoutOnClick}
                                disabled={orderSubmitting}
                                style={{
                                    ...defaultTextStyle,
                                    fontSize: '1.3em',
                                    cursor: 'pointer',
                                }}
                            >
                                {getLocalizedText('review-online-instead')}
                            </a>
                            <div
                                style={{
                                    ...defaultTextStyle,
                                    fontSize: '1.3em',
                                }}
                            >
                                {`(${getLocalizedText('designs-ready-in-24-hours')})`}
                            </div>
                        </div>

                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <div
                            style={{
                                marginTop: 15,

                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ErrorMessage text={submitCoDesignOrderError} />
                            <Button
                                text={getLocalizedText('checkout')}
                                type="primary"
                                large={true}
                                style={{
                                    width: '100%',
                                    maxWidth: 400,
                                }}
                                onClick={this.coDesignCheckoutOnClick}
                                disabled={!this.isBriefComplete() || orderSubmitting}
                            />
                            <div
                                style={{
                                    ...defaultTextStyle,
                                    fontSize: '1em',

                                    marginTop: 10,
                                }}
                            >
                                {getLocalizedText('youll-be-able-to-reschedule')}
                            </div>
                        </div>
                    </Col>
                </Row>
                <LoadingBox
                    show={orderSubmitting}
                    text={getLocalizedText('submitting-order')}
                />
            </Container>
        );
    }
}

const mapStateToProps = state => {
    return {
        selectedProduct: state.selectedProduct,
        selectedProductConfigurations: state.selectedProduct ? (state.selectedProductConfigurationsMap.get(state.selectedProduct.pfid) || []) : [],
        note: state.note,
        uploads: state.uploads,
        uploadRefs: state.uploadRefs,
        previousDesigners: state.previousDesigners,
        selectedDesigner: state.selectedDesigner,
        selectedTimezone: state.selectedTimezone,
        selectedDate: state.selectedDate,
        selectedTime: state.selectedTime,
        name: state.name,
        email: state.email,
        phone: state.phone,
        nameError: state.nameError,
        emailError: state.emailError,
        phoneError: state.phoneError,
        nameErrorsSuppressed: state.nameErrorsSuppressed,
        emailErrorsSuppressed: state.emailErrorsSuppressed,
        phoneErrorsSuppressed: state.phoneErrorsSuppressed,
        availableDates: state.availableDates,
        availableTimes: state.availableTimes,
        availableDatesLoading: state.availableDatesLoading,
        availableTimesLoading: state.availableTimesLoading,
        setAvailableDatesError: state.setAvailableDatesError,
        setAvailableTimesError: state.setAvailableTimesError,
        orderSubmitting: state.orderSubmitting,
        submitCoDesignOrderError: state.submitCoDesignOrderError,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setName: (name) => { dispatch(setName(name)); },
        setEmail: (email) => { dispatch(setEmail(email)); },
        setPhone: (phone) => { dispatch(setPhone(phone)); },
        setNameError: (nameError) => { dispatch(setNameError(nameError)); },
        setEmailError: (emailError) => { dispatch(setEmailError(emailError)); },
        setPhoneError: (phoneError) => { dispatch(setPhoneError(phoneError)); },
        setNameErrorsSuppressed: (nameErrorsSuppressed) => { dispatch(setNameErrorsSuppressed(nameErrorsSuppressed)); },
        setEmailErrorsSuppressed: (emailErrorsSuppressed) => { dispatch(setEmailErrorsSuppressed(emailErrorsSuppressed)); },
        setPhoneErrorsSuppressed: (phoneErrorsSuppressed) => { dispatch(setPhoneErrorsSuppressed(phoneErrorsSuppressed)); },
        setSelectedTimezone: (timezone) => { dispatch(setSelectedTimezone(timezone)); },
        setSelectedDate: (selectedDate) => { dispatch(setSelectedDate(selectedDate)); },
        setSelectedTime: (selectedTime) => { dispatch(setSelectedTime(selectedTime)); },
        setSelectedDesigner: (selectedDesigner) => { dispatch(setSelectedDesigner(selectedDesigner)); },
        getAvailableDates: (timezone) => { dispatch(getAvailableDates(timezone)); },
        getAvailableTimes: (date, timezone) => { dispatch(getAvailableTimes(date, timezone)); },
        submitCoDesignOrder: (coDesignOrderDTO) => { dispatch(submitCoDesignOrder(coDesignOrderDTO)); },
        submitDifyOrder: (difyOrderDTO) => { dispatch(submitDifyOrder(difyOrderDTO)); },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Scheduling);


// WEBPACK FOOTER //
// ./src/pages/Scheduling.js